/**
 * Resolves a CSS variable to its final computed value, handling nested variables
 * @param cssVar - The CSS variable name (with or without -- prefix)
 * @param element - The element to compute styles from (defaults to document.documentElement)
 * @returns The resolved value of the CSS variable
 */
export function resolveCSSVariable(cssVar: string, element: Element = document.documentElement): string {
    // Ensure the variable name starts with --
    const varName = cssVar.startsWith('--') ? cssVar : `--${cssVar}`;
    
    // Get computed styles
    const styles = getComputedStyle(element);
    let value = styles.getPropertyValue(varName).trim();
    
    // Keep resolving if the value is still a var() reference
    while (value.startsWith('var(')) {
        const match = value.match(/var\((--[^,)]+)(?:,\s*([^)]+))?\)/);
        if (match) {
            const nestedVar = match[1];
            const fallback = match[2];
            const nestedValue = styles.getPropertyValue(nestedVar).trim();
            
            if (nestedValue) {
                value = nestedValue;
            } else if (fallback) {
                value = fallback.trim();
            } else {
                break; // No value found and no fallback
            }
        } else {
            break; // Invalid var() syntax
        }
    }
    
    return value;
}

/**
 * Resolves multiple CSS variables at once
 * @param cssVars - Array of CSS variable names
 * @param element - The element to compute styles from (defaults to document.documentElement)
 * @returns Array of resolved values
 */
export function resolveCSSVariables(cssVars: string[], element: Element = document.documentElement): string[] {
    return cssVars.map(cssVar => resolveCSSVariable(cssVar, element));
}

/**
 * Converts RGB/RGBA color string to hex format
 * @param rgb - RGB or RGBA color string
 * @returns Hex color string
 */
export function rgbToHex(rgb: string): string {
    const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);
        return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
    }
    return rgb; // Return as-is if not RGB format
}

/**
 * Resolves a CSS color variable and ensures it's in hex format
 * @param cssVar - The CSS variable name
 * @param element - The element to compute styles from
 * @returns Hex color string
 */
export function resolveCSSColorVariable(cssVar: string, element: Element = document.documentElement): string {
    const color = resolveCSSVariable(cssVar, element);
    
    // If it's already hex, return it
    if (color.startsWith('#')) {
        return color;
    }
    
    // If it's rgb/rgba, convert to hex
    if (color.startsWith('rgb')) {
        return rgbToHex(color);
    }
    
    return color;
}